import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePoke } from "~/hook/domain/usePoke.ts";
import { useUser } from "~/ui/provider/Auth.tsx";
import { useNotification } from "~/ui/provider/Notification.tsx";

export type Effect = "bounce" | "still";

export const EFFECTS: { icon: string; label: string; value: Effect }[] = [
  { icon: "📷", label: "Still", value: "still" },
  { icon: "🏀", label: "Bounce", value: "bounce" },
];

const FRAME_RATE = 15;
const PRE_CAPTURE_SECONDS = 1.5;
const POST_CAPTURE_SECONDS = 1.5;
const PRE_CAPTURE_FRAMES = Math.ceil(FRAME_RATE * PRE_CAPTURE_SECONDS);

export const useCamera = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async (facingMode: "environment" | "user") => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode, height: 720, width: 720 },
    });
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    }
  }, []);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
  }, []);

  const reattach = useCallback(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;
    const onReady = () => {
      video.removeEventListener("loadedmetadata", onReady);
      void video.play();
    };
    video.addEventListener("loadedmetadata", onReady);
    video.load();
  }, []);

  return { reattach, start, stop, videoRef };
};

export const useRollingCapture = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  facingMode: "environment" | "user",
) => {
  const bufferRef = useRef<ImageBitmap[]>([]);
  const intervalRef = useRef<null | ReturnType<typeof setInterval>>(null);
  const postCountRef = useRef(-1);

  const [isCapturing, setIsCapturing] = useState(false);
  const [frames, setFrames] = useState<ImageBitmap[] | null>(null);

  const grabFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    const offscreen = new OffscreenCanvas(w, h);
    const ctx = offscreen.getContext("2d");
    if (!ctx) return;

    if (facingMode === "user") {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, w, h);

    void createImageBitmap(offscreen).then((bmp) => {
      bufferRef.current.push(bmp);

      if (postCountRef.current >= 0) {
        postCountRef.current++;
        const postTarget = Math.ceil(FRAME_RATE * POST_CAPTURE_SECONDS);
        if (postCountRef.current >= postTarget) {
          postCountRef.current = -1;
          const total = PRE_CAPTURE_FRAMES + postTarget;
          const result =
            bufferRef.current.length > total
              ? bufferRef.current.slice(bufferRef.current.length - total)
              : [...bufferRef.current];
          setFrames(result);
          setIsCapturing(false);
        }
      } else {
        while (bufferRef.current.length > PRE_CAPTURE_FRAMES) {
          bufferRef.current.shift()?.close();
        }
      }
    });
  }, [videoRef, facingMode]);

  const startBuffering = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(grabFrame, 1000 / FRAME_RATE);
  }, [grabFrame]);

  const stopBuffering = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const capture = useCallback(() => {
    postCountRef.current = 0;
    setIsCapturing(true);
  }, []);

  const clearBuffer = useCallback(() => {
    bufferRef.current.forEach((bmp) => {
      bmp.close();
    });
    bufferRef.current = [];
  }, []);

  const reset = useCallback(() => {
    stopBuffering();
    setFrames(null);
    clearBuffer();
    postCountRef.current = -1;
  }, [stopBuffering, clearBuffer]);

  return { capture, frames, isCapturing, reset, startBuffering, stopBuffering };
};

export const generateVideoBlob = (frames: ImageBitmap[]): Promise<Blob> => {
  if (frames.length === 0) return Promise.resolve(new Blob());

  const first = frames[0];
  const canvas = document.createElement("canvas");
  canvas.width = first.width;
  canvas.height = first.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(new Blob());

  ctx.drawImage(first, 0, 0);

  const stream = canvas.captureStream(0);
  const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
    ? "video/webm;codecs=vp9"
    : "video/webm";
  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks: Blob[] = [];

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  return new Promise((resolve) => {
    recorder.onstop = () => {
      resolve(new Blob(chunks, { type: "video/webm" }));
    };
    recorder.start();

    let i = 0;
    const tick = () => {
      if (i >= frames.length) {
        recorder.stop();
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(frames[i], 0, 0);
      const track = stream.getVideoTracks()[0] as
        (MediaStreamTrack & { requestFrame?: () => void }) | undefined;
      track?.requestFrame?.();
      i++;
      setTimeout(tick, 1000 / FRAME_RATE);
    };
    tick();
  });
};

export const usePokeWithPicture = (email: string, close: () => void) => {
  const [facingMode, setFacingMode] = useState<"environment" | "user">("user");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const { reattach, start, stop, videoRef } = useCamera();
  const { capture, frames, isCapturing, reset, startBuffering, stopBuffering } =
    useRollingCapture(videoRef, facingMode);
  const [effect, setEffect] = useState<Effect>("bounce");
  const [isUploading, setIsUploading] = useState(false);

  const { isMutating, trigger } = usePoke();
  const { client } = useUser();
  const push = useNotification();
  const { t } = useLingui();

  const [cameraReady, setCameraReady] = useState(false);
  const captured = frames !== null && frames.length > 0;

  useEffect(() => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        setHasMultipleCameras(videoDevices.length > 1);
      })
      .catch(() => {
        // Ignore
      });
  }, []);

  useEffect(() => {
    let active = true;
    reset();
    void start(facingMode).then(() => {
      if (active) {
        setCameraReady(true);
      }
    });
    return () => {
      active = false;
      setCameraReady(false);
      stop();
      stopBuffering();
    };
  }, [facingMode, start, stop, stopBuffering, reset]);

  useEffect(() => {
    if (cameraReady) {
      startBuffering();
    }
  }, [cameraReady, startBuffering]);

  const handleSwitchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const handleCapture = () => {
    capture();
  };

  const handleRetake = () => {
    reset();
    startBuffering();
    requestAnimationFrame(() => {
      reattach();
    });
  };

  const handleSend = async () => {
    if (!frames || frames.length === 0) return;
    setIsUploading(true);
    try {
      const videoBlob = await generateVideoBlob(frames);
      const formData = new FormData();
      formData.append("video", videoBlob, "capture.webm");
      const videoUrl = await client
        .post("util/video", { body: formData })
        .text();
      await trigger({
        email,
        payload: { effect, type: "picture", videoUrl },
      });
      close();
    } catch {
      push({ content: t`다시 시도해주세요.` });
    } finally {
      setIsUploading(false);
    }
  };

  const disabled = isMutating || isUploading;

  return {
    cameraReady,
    captured,
    disabled,
    effect,
    facingMode,
    frames,
    handleCapture,
    handleRetake,
    handleSend,
    handleSwitchCamera,
    hasMultipleCameras,
    isCapturing,
    setEffect,
    videoRef,
  };
};
